import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Select,
  Checkbox,
  Banner,
  Badge,
  Tag,
  Box,
  RadioButton,
} from '@shopify/polaris';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../providers/ApiProvider';
import { useStore } from '../../providers/StoreProvider';
import { LocationPickerModal } from '../../components/LocationPickerModal';
import {
  CatalogPickerModal,
  idMatch,
  type CatalogItem as PickerItem,
} from '../../components/CatalogPickerModal';
import {
  CustomerSegmentField,
  segmentIdsFromValue,
  segmentNamesFromValue,
  segmentValueFromEligibility,
} from '../../components/CustomerSegmentField';
import {
  CampaignScheduleFields,
  defaultScheduleValue,
  scheduleFromIso,
  scheduleToIso,
  type CampaignScheduleValue,
} from '../../components/CampaignScheduleFields';
import { DEFAULT_POPUP_DESIGN } from '../widgets/popupDefaults';
import { PopupPreview } from '../widgets/PopupPreview';
import { DEFAULT_CONGRATS_DESIGN } from '../widgets/congratsDefaults';
import { CongratsPreview } from '../widgets/CongratsPreview';
import {
  DEFAULT_PROMO_TITLE,
  DEFAULT_PROMO_SUBTITLE_BXGY,
  offeredAmount,
  parseMoneyAmount,
} from '../widgets/promoDefaults';
import { CampaignPromoWidgetCard } from '../../components/CampaignPromoWidgetCard';
import {
  ContextualSaveBar,
  SavingOverlay,
  isShopifyEmbedded,
  showAppToast,
  useUnsavedChanges,
} from '../../components/ContextualSaveBar';
import { SelectedProductRow } from '../../components/SelectedProductRow';
import {
  CampaignNameEditor,
  campaignPageLabel,
} from '../../components/CampaignNameEditor';
import {
  generateBuyXGetYCode,
  resolveUniqueDiscountCode,
} from '../../lib/discountCode';
import { t } from '../../i18n';

/** 0 = BXGY vs BOGO picker (new campaigns only) */
type MainStep = 0 | 1 | 2;
type SideSection = 'buys' | 'gets' | 'stacking' | 'advanced';
type ScopeType = 'products' | 'collections';
/** BXGY = different reward products; BOGO = same product free */
type OfferMode = 'bxgy' | 'bogo';

interface CatalogItem {
  id: string;
  title: string;
  kind: 'product' | 'collection' | 'variant';
  handle?: string;
  imageUrl?: string | null;
  /** Gift quantity for this line (Customer gets only — BXGY) */
  quantity?: number;
  productId?: string;
  variantId?: string | null;
  price?: string | number | null;
  /** false when product/variant is unavailable for sale */
  available?: boolean;
  outOfStock?: boolean;
}

export function BuyXGetYWizard() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const api = useApi();
  const { currency, moneyPrefix, formatMoney } = useStore();

  const campaignId =
    routeId && routeId !== 'new' && routeId !== 'configure' ? routeId : null;
  const isEdit = Boolean(campaignId);
  const [loadingCampaign, setLoadingCampaign] = useState(isEdit);

  // New campaigns start on mode picker; edit skips to settings
  const [mainStep, setMainStep] = useState<MainStep>(isEdit ? 1 : 0);
  const [section, setSection] = useState<SideSection>('buys');
  const [offerMode, setOfferMode] = useState<OfferMode | null>(
    isEdit ? null : null,
  );
  const isBogo = offerMode === 'bogo';
  const isBxgy = offerMode === 'bxgy';

  const [name, setName] = useState('Buy X Get Y');
  const [schedule, setSchedule] = useState<CampaignScheduleValue>(defaultScheduleValue);

  // Customer buys
  const [buyQuantity, setBuyQuantity] = useState('2');
  const [buyScope, setBuyScope] = useState<ScopeType>('products');
  const [buySelected, setBuySelected] = useState<CatalogItem[]>([]);

  // Customer gets (BXGY only — separate products). BOGO mirrors buys.
  const [getScope, setGetScope] = useState<ScopeType>('products');
  const [getSelected, setGetSelected] = useState<CatalogItem[]>([]);
  /** BOGO: free units of the same product (Y). Also used as gift limit default. */
  const [getFreeQuantity, setGetFreeQuantity] = useState('1');
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('100');
  const [discountCode, setDiscountCode] = useState(() => generateBuyXGetYCode());
  /** Cap how many gift units customer can take from the list (BXGY) */
  const [giftLimitEnabled, setGiftLimitEnabled] = useState(false);
  const [giftLimitCount, setGiftLimitCount] = useState('1');

  // Stacking
  const [priority, setPriority] = useState('10');
  const [stopOnMatch, setStopOnMatch] = useState(true);

  // Advanced
  const [usageLimitEnabled, setUsageLimitEnabled] = useState(false);
  const [totalUses, setTotalUses] = useState('');
  const [maxUsesPerCustomer, setMaxUsesPerCustomer] = useState('1');
  type Eligibility = 'all' | 'segments' | 'utm' | 'location';
  const [eligibility, setEligibility] = useState<Eligibility>('all');
  const [segmentValue, setSegmentValue] = useState('');
  const [utmValue, setUtmValue] = useState('');
  const [locationCodes, setLocationCodes] = useState<string[]>([]);
  const [locationLabels, setLocationLabels] = useState<Record<string, string>>({});
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  // Display
  type GiftMethod = 'automatic' | 'customer_chooses';
  const [giftMethod, setGiftMethod] = useState<GiftMethod>('automatic');
  const [widgetTitle, setWidgetTitle] = useState('You unlocked a free gift!');
  const [widgetDescription, setWidgetDescription] = useState(
    'Add your free product before checkout.',
  );
  const [congratsBars, setCongratsBars] = useState(true);
  const [congratsTitle, setCongratsTitle] = useState(DEFAULT_CONGRATS_DESIGN.title);
  const [promoWidgetEnabled, setPromoWidgetEnabled] = useState(false);
  const [promoWidgetTitle, setPromoWidgetTitle] = useState(DEFAULT_PROMO_TITLE);
  const [promoWidgetSubtitle, setPromoWidgetSubtitle] = useState(
    DEFAULT_PROMO_SUBTITLE_BXGY,
  );
  const [congratsSubtitle, setCongratsSubtitle] = useState(
    DEFAULT_CONGRATS_DESIGN.subtitle,
  );

  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseTarget, setBrowseTarget] = useState<'buy' | 'get'>('buy');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const embedded = isShopifyEmbedded();

  const formSnapshot = useMemo(
    () => ({
      offerMode,
      name,
      schedule,
      buyQuantity,
      buyScope,
      buySelected,
      getScope,
      getSelected,
      getFreeQuantity,
      discountType,
      discountValue,
      discountCode,
      giftLimitEnabled,
      giftLimitCount,
      priority,
      stopOnMatch,
      usageLimitEnabled,
      totalUses,
      maxUsesPerCustomer,
      eligibility,
      segmentValue,
      utmValue,
      locationCodes,
      locationLabels,
      giftMethod,
      widgetTitle,
      widgetDescription,
      congratsBars,
      congratsTitle,
      congratsSubtitle,
      promoWidgetEnabled,
      promoWidgetTitle,
      promoWidgetSubtitle,
    }),
    [
      offerMode,
      name,
      schedule,
      buyQuantity,
      buyScope,
      buySelected,
      getScope,
      getSelected,
      getFreeQuantity,
      discountType,
      discountValue,
      discountCode,
      giftLimitEnabled,
      giftLimitCount,
      priority,
      stopOnMatch,
      usageLimitEnabled,
      totalUses,
      maxUsesPerCustomer,
      eligibility,
      segmentValue,
      utmValue,
      locationCodes,
      locationLabels,
      giftMethod,
      widgetTitle,
      widgetDescription,
      congratsBars,
      congratsTitle,
      congratsSubtitle,
      promoWidgetEnabled,
      promoWidgetTitle,
      promoWidgetSubtitle,
    ],
  );
  const { dirty, markClean, getBaseline, markCleanAfterDiscard } =
    useUnsavedChanges(formSnapshot);

  useEffect(() => {
    if (loadingCampaign) return;
    markClean(formSnapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCampaign]);

  const canGoNextFromBuys =
    Number(buyQuantity) > 0 &&
    buySelected.length > 0 &&
    (!isBogo || Number(getFreeQuantity) > 0);
  const canGoNextFromGets =
    isBogo ||
    (getSelected.length > 0 &&
      getSelected.every((i) => (i.quantity ?? 1) >= 1));
  /** Free units Y — BOGO uses getFreeQuantity; BXGY sums per-line gift qty */
  const totalGetQty = isBogo
    ? Math.max(1, Number(getFreeQuantity) || 1)
    : getSelected.reduce((s, i) => s + (i.quantity ?? 1), 0);

  const sideNavOrder: SideSection[] = isBogo
    ? ['buys', 'stacking', 'advanced']
    : ['buys', 'gets', 'stacking', 'advanced'];

  const browseScope = browseTarget === 'buy' ? buyScope : getScope;

  useEffect(() => {
    if (!campaignId) {
      setLoadingCampaign(false);
      return;
    }
    setLoadingCampaign(true);
    api
      .get<{
        name: string;
        priority?: number;
        startsAt?: string | null;
        endsAt?: string | null;
        maxUsesTotal?: number | null;
        maxUsesPerCustomer?: number | null;
        config?: Record<string, unknown>;
      }>(`/campaigns/${campaignId}`)
      .then((c) => {
        setName(c.name || 'Buy X Get Y');
        setSchedule(scheduleFromIso(c.startsAt, c.endsAt));

        const cfg = (c.config || {}) as Record<string, unknown>;
        const bx = (cfg.buyXGetY || {}) as Record<string, unknown>;
        const display = (cfg.display || {}) as Record<string, unknown>;
        const stacking = (cfg.stacking || {}) as Record<string, unknown>;
        // Column first; fall back to config.stacking.priority
        const stackP =
          stacking.priority != null && Number(stacking.priority) >= 0
            ? Number(stacking.priority)
            : null;
        const colP =
          c.priority != null && Number.isFinite(Number(c.priority))
            ? Number(c.priority)
            : null;
        setPriority(String(colP != null ? colP : stackP != null ? stackP : 10));
        const advanced = (cfg.advanced || {}) as Record<string, unknown>;
        const elig = (advanced.customerEligibility || {}) as Record<
          string,
          unknown
        >;
        const discount = (bx.discount || {}) as Record<string, unknown>;

        const advUsageOff = advanced.usageLimitEnabled === false;
        const advTotal =
          advanced.totalUses != null && Number(advanced.totalUses) > 0
            ? Number(advanced.totalUses)
            : null;
        const topTotal =
          c.maxUsesTotal != null && Number(c.maxUsesTotal) > 0
            ? Number(c.maxUsesTotal)
            : null;
        if (!advUsageOff && (advanced.usageLimitEnabled === true || topTotal || advTotal)) {
          setUsageLimitEnabled(true);
          setTotalUses(String(topTotal ?? advTotal ?? ''));
        } else {
          setUsageLimitEnabled(false);
          setTotalUses('');
        }
        const maxPer =
          advanced.maxUsesPerCustomer != null &&
          Number(advanced.maxUsesPerCustomer) > 0
            ? Number(advanced.maxUsesPerCustomer)
            : c.maxUsesPerCustomer != null && Number(c.maxUsesPerCustomer) > 0
              ? Number(c.maxUsesPerCustomer)
              : 1;
        setMaxUsesPerCustomer(String(Math.max(1, Math.floor(maxPer) || 1)));

        if (bx.buyQuantity != null) setBuyQuantity(String(bx.buyQuantity));
        // Restore BXGY vs BOGO mode
        if (bx.sameSku === true || bx.offerMode === 'bogo') {
          setOfferMode('bogo');
          setMainStep(1);
        } else {
          setOfferMode('bxgy');
          setMainStep(1);
        }
        if (bx.getQuantity != null) {
          setGetFreeQuantity(String(bx.getQuantity));
        }
        if (bx.buyScope === 'products' || bx.buyScope === 'collections') {
          setBuyScope(bx.buyScope);
        }
        if (bx.getScope === 'products' || bx.getScope === 'collections') {
          setGetScope(bx.getScope);
        }
        if (Array.isArray(bx.buyItems)) {
          setBuySelected(bx.buyItems as CatalogItem[]);
        } else if (Array.isArray(bx.buyProductIds)) {
          setBuySelected(
            (bx.buyProductIds as string[]).map((pid) => ({
              id: pid,
              title: pid,
              kind: 'product' as const,
            })),
          );
        }
        // Restore "customer gets" selection matching getScope.
        // Never load expanded product rows when scope is collections — that
        // used to pollute getCollectionIds with Product/* on the next save.
        if (bx.getScope === 'collections') {
          const collItems = Array.isArray(bx.getItems)
            ? (bx.getItems as CatalogItem[]).filter(
                (i) =>
                  i?.kind === 'collection' ||
                  String(i?.id || '').includes('/Collection/'),
              )
            : [];
          if (collItems.length) {
            setGetSelected(collItems.map((i) => ({ ...i, kind: 'collection' as const })));
          } else if (Array.isArray(bx.getCollectionIds) && bx.getCollectionIds.length) {
            setGetSelected(
              (bx.getCollectionIds as string[])
                .filter(
                  (id) =>
                    String(id).includes('/Collection/') ||
                    (!String(id).includes('/Product/') &&
                      !String(id).includes('/ProductVariant/')),
                )
                .map((cid) => ({
                  id: cid,
                  title: cid,
                  kind: 'collection' as const,
                  quantity: 1,
                })),
            );
          }
        } else if (Array.isArray(bx.getItems)) {
          const products = (bx.getItems as CatalogItem[]).filter(
            (i) =>
              i?.kind !== 'collection' &&
              !String(i?.id || '').includes('/Collection/'),
          );
          if (products.length) setGetSelected(products);
        } else if (Array.isArray(bx.getProductIds)) {
          setGetSelected(
            (bx.getProductIds as string[]).map((pid) => ({
              id: pid,
              title: pid,
              kind: 'product' as const,
              quantity: 1,
            })),
          );
        }
        if (typeof bx.giftLimitEnabled === 'boolean') {
          setGiftLimitEnabled(bx.giftLimitEnabled);
        }
        if (bx.giftLimitCount != null) setGiftLimitCount(String(bx.giftLimitCount));
        if (discount.code) setDiscountCode(String(discount.code));
        if (discount.type) setDiscountType(String(discount.type));
        if (discount.value != null) setDiscountValue(String(discount.value));
        else if (bx.discountPercent != null) {
          setDiscountValue(String(bx.discountPercent));
        }

        // BXGY (different products): only customer chooses — never restore automatic
        const loadedBogo = bx.sameSku === true || bx.offerMode === 'bogo';
        if (!loadedBogo) {
          setGiftMethod('customer_chooses');
        } else if (bx.autoAdd === true || bx.selectionMode === 'auto') {
          setGiftMethod('automatic');
        } else if (display.giftMethod === 'customer_chooses') {
          setGiftMethod('customer_chooses');
        }
        if (typeof display.title === 'string') setWidgetTitle(display.title);
        if (typeof display.description === 'string') {
          setWidgetDescription(display.description);
        }
        if (typeof display.congratsBars === 'boolean') {
          setCongratsBars(display.congratsBars);
        }
        if (typeof display.congratsTitle === 'string') {
          setCongratsTitle(display.congratsTitle);
        }
        if (typeof display.congratsSubtitle === 'string') {
          setCongratsSubtitle(display.congratsSubtitle);
        }
        setPromoWidgetEnabled(display.promoWidgetEnabled === true);
        if (typeof display.promoWidgetTitle === 'string') {
          setPromoWidgetTitle(display.promoWidgetTitle);
        }
        if (typeof display.promoWidgetSubtitle === 'string') {
          setPromoWidgetSubtitle(display.promoWidgetSubtitle);
        }
        if (typeof stacking.stopOnMatch === 'boolean') {
          setStopOnMatch(stacking.stopOnMatch);
        }
        if (elig.type === 'segments' || elig.type === 'utm' || elig.type === 'location') {
          setEligibility(elig.type);
        } else {
          setEligibility('all');
        }
        setSegmentValue(segmentValueFromEligibility(elig));
        setUtmValue(typeof elig.utm === 'string' ? elig.utm : '');
        if (Array.isArray(elig.locationCodes)) {
          setLocationCodes(elig.locationCodes as string[]);
        } else {
          setLocationCodes([]);
        }
        if (Array.isArray(elig.locations)) {
          const labels: Record<string, string> = {};
          for (const loc of elig.locations as Array<{ code: string; name?: string }>) {
            labels[loc.code] = loc.name || loc.code;
          }
          setLocationLabels(labels);
        } else {
          setLocationLabels({});
        }
      })
      .catch((e: Error) => setError(e.message || 'Failed to load campaign'))
      .finally(() => setLoadingCampaign(false));
  }, [api, campaignId]);

  function openBrowse(target: 'buy' | 'get') {
    setBrowseTarget(target);
    setBrowseOpen(true);
  }

  function stripProductId(raw: string | null | undefined): string {
    if (!raw) return '';
    return (
      String(raw)
        .replace(/^gid:\/\/shopify\/Product\//, '')
        .replace(/\D/g, '') || String(raw)
    );
  }

  function stripVariantId(raw: string | null | undefined): string {
    if (!raw) return '';
    return (
      String(raw)
        .replace(/^gid:\/\/shopify\/ProductVariant\//, '')
        .replace(/\D/g, '') || String(raw)
    );
  }

  /**
   * Map catalog picker row → stable selection row.
   * CRITICAL: variant rows must keep a variant-unique `id`. Using productId as
   * id made every variant of a product match each other, so click #2 removed #1
   * and multi-select-all thrashed to 0 selected.
   */
  function mapPickerItem(item: PickerItem): CatalogItem {
    const isCollection = item.kind === 'collection';
    const isVariant =
      item.kind === 'variant' ||
      (!!item.variantId && item.kind !== 'product' && item.kind !== 'collection');

    const productId = isCollection
      ? undefined
      : stripProductId(
          item.productId ||
            (item.kind === 'product' ? item.id : undefined) ||
            (item.kind === 'variant' ? item.productId : undefined) ||
            '',
        ) || undefined;

    const variantId = isCollection
      ? null
      : stripVariantId(
          item.variantId ||
            (item.kind === 'variant' ? item.id : '') ||
            '',
        ) || null;

    // Variant row → id = variant; product/collection → id = product/collection
    const id = isCollection
      ? item.id
      : isVariant && variantId
        ? variantId
        : productId || stripProductId(item.id) || item.id;

    return {
      id,
      title: item.title,
      kind: isCollection
        ? 'collection'
        : isVariant && variantId
          ? 'variant'
          : 'product',
      handle: item.handle,
      imageUrl: item.imageUrl || null,
      quantity: 1,
      productId,
      variantId: isVariant ? variantId : variantId || null,
      available: item.available !== false,
      outOfStock: item.available === false,
    };
  }

  function pickerRowMatch(a: CatalogItem, b: CatalogItem): boolean {
    // Collections
    if (a.kind === 'collection' || b.kind === 'collection') {
      return idMatch(String(a.id), String(b.id));
    }
    // Both have concrete variants → match variants only
    if (a.variantId && b.variantId) {
      return idMatch(String(a.variantId), String(b.variantId));
    }
    // Same explicit row id
    if (idMatch(String(a.id), String(b.id))) return true;
    // Product-level rows (no variant on either) match by productId
    if (
      !a.variantId &&
      !b.variantId &&
      a.productId &&
      b.productId &&
      idMatch(String(a.productId), String(b.productId))
    ) {
      return true;
    }
    return false;
  }

  function applyPickerToggle(
    prev: CatalogItem[],
    mapped: CatalogItem,
    action?: 'add' | 'remove',
  ): CatalogItem[] {
    const exists = prev.some((x) => pickerRowMatch(x, mapped));
    if (action === 'add') {
      return exists ? prev : [...prev, mapped];
    }
    if (action === 'remove') {
      return exists ? prev.filter((x) => !pickerRowMatch(x, mapped)) : prev;
    }
    // toggle
    return exists
      ? prev.filter((x) => !pickerRowMatch(x, mapped))
      : [...prev, mapped];
  }

  function togglePickerItem(item: PickerItem) {
    const mapped = mapPickerItem(item);
    if (browseTarget === 'buy') {
      setBuySelected((prev) => applyPickerToggle(prev, mapped));
    } else {
      setGetSelected((prev) => applyPickerToggle(prev, mapped));
    }
  }

  function togglePickerMany(items: PickerItem[], action: 'add' | 'remove') {
    const mapped = items.map(mapPickerItem);
    if (browseTarget === 'buy') {
      setBuySelected((prev) => {
        let next = prev;
        for (const m of mapped) next = applyPickerToggle(next, m, action);
        return next;
      });
    } else {
      setGetSelected((prev) => {
        let next = prev;
        for (const m of mapped) next = applyPickerToggle(next, m, action);
        return next;
      });
    }
  }

  /** Checkbox ids: variant rows → variant id only; product-level → product id only */
  function pickerSelectedIds(list: CatalogItem[]): string[] {
    const out: string[] = [];
    for (const i of list) {
      if (i.kind === 'collection') {
        if (i.id) out.push(i.id);
        continue;
      }
      if (i.variantId && (i.kind === 'variant' || i.variantId)) {
        // Specific variant pick — do NOT include productId (would mark whole product)
        out.push(String(i.variantId));
        if (i.id && !idMatch(String(i.id), String(i.variantId))) {
          // only if id is also a variant-ish token
          if (String(i.id) === String(i.variantId) || i.kind === 'variant') {
            out.push(String(i.id));
          }
        }
        continue;
      }
      // Product-level
      if (i.productId) out.push(String(i.productId));
      if (i.id) out.push(String(i.id));
    }
    return out;
  }

  function updateGetQty(id: string, qty: string) {
    const n = Math.max(1, parseInt(qty, 10) || 1);
    setGetSelected((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity: n } : i)),
    );
  }

  function sideNavItem(key: SideSection, label: string, icon: string) {
    const active = section === key && mainStep === 1;
    return (
      <button
        type="button"
        key={key}
        onClick={() => {
          setMainStep(1);
          setSection(key);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          textAlign: 'left',
          padding: '10px 12px',
          borderRadius: 8,
          border: active ? '1.5px solid #2c6ecb' : '1.5px solid transparent',
          background: active ? '#f2f7fe' : 'transparent',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: active ? 600 : 500,
          color: '#202223',
        }}
      >
        <span style={{ fontSize: 16 }}>{icon}</span>
        {label}
      </button>
    );
  }

  function scopeSelect(
    value: ScopeType,
    onChange: (v: ScopeType) => void,
    selected: CatalogItem[],
    setSelected: (items: CatalogItem[]) => void,
    target: 'buy' | 'get',
    /** Per-line quantity for Customer gets */
    withLineQty?: boolean,
  ) {
    return (
      <BlockStack gap="300">
        <Select
          label={t("Applies to")}
          options={[
            { label: t("Specific products"), value: 'products' },
            { label: t("Collections"), value: 'collections' },
          ]}
          value={value}
          onChange={(v) => {
            onChange(v as ScopeType);
            setSelected([]);
          }}
        />
        <InlineStack gap="300" blockAlign="center" wrap>
          <Text as="span" tone="subdued">
            {selected.length === 0
              ? value === 'products'
                ? 'No products selected'
                : 'No collections selected'
              : `${selected.length} ${value === 'products' ? 'product' : 'collection'}${selected.length === 1 ? '' : 's'} selected`}
          </Text>
          <Button onClick={() => openBrowse(target)}>
            {value === 'products' ? 'Select products' : 'Select collections'}
          </Button>
        </InlineStack>
        {selected.length > 0 && (
          <BlockStack gap="200">
            {selected.map((item) =>
              withLineQty || item.kind === 'product' || item.kind === 'variant' ? (
                <SelectedProductRow
                  key={item.id}
                  product={{
                    id: item.id,
                    title: item.title,
                    imageUrl: item.imageUrl,
                    quantity: item.quantity ?? 1,
                    outOfStock:
                      item.outOfStock === true || item.available === false,
                  }}
                  showQuantity={Boolean(withLineQty)}
                  onQuantityChange={
                    withLineQty
                      ? (quantity) => updateGetQty(item.id, String(quantity))
                      : undefined
                  }
                  onRemove={() =>
                    setSelected(selected.filter((x) => x.id !== item.id))
                  }
                />
              ) : (
                <Box
                  key={item.id}
                  padding="200"
                  background="bg-surface-secondary"
                  borderRadius="200"
                >
                  <InlineStack align="space-between" blockAlign="center" wrap gap="300">
                    <InlineStack gap="200" blockAlign="center">
                      <Badge>{t("Collection")}</Badge>
                      <Text as="span" fontWeight="semibold">
                        {item.title}
                      </Text>
                    </InlineStack>
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() =>
                        setSelected(selected.filter((x) => x.id !== item.id))
                      }
                    >
                      {t("Remove")}
                    </Button>
                  </InlineStack>
                </Box>
              ),
            )}
          </BlockStack>
        )}
      </BlockStack>
    );
  }

  function validateBeforeSave(): {
    message: string;
    step: MainStep;
    section: SideSection;
  } | null {
    if (!offerMode) {
      return {
        message: 'Choose BXGY (different products) or BOGO (same product).',
        step: 0,
        section: 'buys',
      };
    }
    if (!name.trim()) {
      return {
        message: 'Enter a campaign name before saving.',
        step: 1,
        section: 'buys',
      };
    }
    if (Number(buyQuantity) < 1) {
      return {
        message: 'Set how many products customers must buy (at least 1).',
        step: 1,
        section: 'buys',
      };
    }
    if (buySelected.length === 0) {
      return {
        message:
          buyScope === 'collections'
            ? 'Select at least one collection for “Customer buys”.'
            : 'Select at least one product for “Customer buys”.',
        step: 1,
        section: 'buys',
      };
    }
    if (isBogo) {
      if (Number(getFreeQuantity) < 1) {
        return {
          message: 'Set how many free units of the same product customers get (at least 1).',
          step: 1,
          section: 'buys',
        };
      }
    } else {
      if (getSelected.length === 0) {
        return {
          message:
            getScope === 'collections'
              ? 'Select at least one collection for “Customer gets”.'
              : 'Select at least one free gift product for “Customer gets”.',
          step: 1,
          section: 'gets',
        };
      }
      if (!getSelected.every((i) => (i.quantity ?? 1) >= 1)) {
        return {
          message: 'Each free gift line needs a quantity of at least 1.',
          step: 1,
          section: 'gets',
        };
      }
      if (giftLimitEnabled && Number(giftLimitCount) < 1) {
        return {
          message: 'Gift limit must be at least 1, or turn off the gift limit.',
          step: 1,
          section: 'gets',
        };
      }
    }
    if (!discountCode.trim()) {
      return {
        message: 'Enter a discount code for this offer.',
        step: 1,
        section: isBogo ? 'buys' : 'gets',
      };
    }
    if (discountType !== 'free' && (discountValue === '' || Number(discountValue) < 0)) {
      return {
        message: 'Set a valid discount value.',
        step: 1,
        section: isBogo ? 'buys' : 'gets',
      };
    }
    if (eligibility === 'segments' && !segmentIdsFromValue(segmentValue).length) {
      return {
        message: 'Select at least one customer segment, or set eligibility to all customers.',
        step: 1,
        section: 'advanced',
      };
    }
    if (eligibility === 'utm' && !utmValue.trim()) {
      return {
        message: 'Enter a UTM value, or set eligibility to all customers.',
        step: 1,
        section: 'advanced',
      };
    }
    if (eligibility === 'location' && locationCodes.length === 0) {
      return {
        message: 'Select at least one location, or set eligibility to all customers.',
        step: 1,
        section: 'advanced',
      };
    }
    if (usageLimitEnabled && (!totalUses || Number(totalUses) < 1)) {
      return {
        message: 'Enter a total usage limit greater than 0, or turn off the usage limit.',
        step: 1,
        section: 'advanced',
      };
    }
    return null;
  }

  async function save(activate: boolean) {
    const invalid = validateBeforeSave();
    if (invalid) {
      setMainStep(invalid.step);
      setSection(invalid.section);
      setError(invalid.message);
      setMessage(null);
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const uniqueCode = await resolveUniqueDiscountCode(
        api,
        'BUYXGETY_',
        discountCode,
        campaignId || undefined,
      );
      if (uniqueCode !== discountCode.trim().toUpperCase()) {
        setDiscountCode(uniqueCode);
      }

      // BOGO: reward = same products/collections as buys (no separate get picker)
      const effectiveGetScope: ScopeType = isBogo ? buyScope : getScope;
      const freeY = Math.max(1, Number(totalGetQty) || 1);
      const effectiveGetSelected: CatalogItem[] = isBogo
        ? buySelected.map((i) => ({
            ...i,
            kind:
              buyScope === 'collections'
                ? ('collection' as const)
                : i.kind === 'variant'
                  ? ('variant' as const)
                  : ('product' as const),
            // Free unit count is freeY overall; each line is a catalog option
            quantity: 1,
          }))
        : getSelected;

      /** Always product id — never store a variant id as product id (breaks unlock). */
      const productKey = (i: CatalogItem) => {
        const fromProduct = String(i.productId || '')
          .replace(/^gid:\/\/shopify\/Product\//, '')
          .replace(/\D/g, '');
        if (fromProduct) return fromProduct;
        const raw = String(i.id || '');
        if (
          i.kind === 'variant' ||
          raw.includes('ProductVariant') ||
          i.variantId
        ) {
          // Variant row without productId — cannot safely use variant id
          return '';
        }
        return (
          raw
            .replace(/^gid:\/\/shopify\/Product\//, '')
            .replace(/\D/g, '') || raw
        );
      };

      const stripVid = (v: string | null | undefined) => {
        if (!v) return '';
        return (
          String(v)
            .replace(/^gid:\/\/shopify\/ProductVariant\//, '')
            .replace(/\D/g, '') || String(v)
        );
      };

      /**
       * Group Customer gets variants of the same product into ONE row with
       * allowedVariantIds / selectedVariants so the storefront popup shows
       * one product + size picker (not S/M/L as separate gift cards).
       */
      const groupGetItemsByProduct = (
        items: CatalogItem[],
      ): Array<Record<string, unknown>> => {
        type Acc = {
          id: string;
          productId: string;
          title: string;
          imageUrl: string | null;
          quantity: number;
          kind: 'product';
          variantId: string | null;
          allowedVariantIds: string[];
          selectedVariants: Array<{
            id: string;
            title: string;
            price?: string | null;
          }>;
        };
        const byPid = new Map<string, Acc>();
        for (const i of items) {
          if (
            i.kind === 'collection' ||
            String(i.id || '').includes('/Collection/')
          ) {
            continue;
          }
          const pid = productKey(i);
          if (!pid) continue;
          const vid = stripVid(i.variantId || (i.kind === 'variant' ? i.id : ''));
          // Clean product title (drop " — Multicolor / S" from variant rows)
          let title = String(i.title || 'Gift').trim();
          const sep = title.indexOf(' — ');
          if (sep > 0) title = title.slice(0, sep).trim() || title;

          const existing = byPid.get(pid);
          if (!existing) {
            byPid.set(pid, {
              id: pid,
              productId: pid,
              title,
              imageUrl: i.imageUrl || null,
              quantity: Math.max(1, Number(i.quantity) || 1),
              kind: 'product',
              variantId: vid || null,
              allowedVariantIds: vid ? [vid] : [],
              selectedVariants: vid
                ? [
                    {
                      id: vid,
                      title: String(i.title || '').includes(' — ')
                        ? String(i.title).split(' — ').slice(1).join(' — ')
                        : 'Default',
                      price: i.price != null ? String(i.price) : null,
                    },
                  ]
                : [],
            });
            continue;
          }
          if (vid && !existing.allowedVariantIds.includes(vid)) {
            existing.allowedVariantIds.push(vid);
            existing.selectedVariants.push({
              id: vid,
              title: String(i.title || '').includes(' — ')
                ? String(i.title).split(' — ').slice(1).join(' — ')
                : 'Default',
              price: i.price != null ? String(i.price) : null,
            });
          }
          // Multi-variant → no single pin
          if (existing.allowedVariantIds.length > 1) {
            existing.variantId = null;
          }
          existing.quantity = Math.max(
            existing.quantity,
            Math.max(1, Number(i.quantity) || 1),
          );
          if (!existing.imageUrl && i.imageUrl) existing.imageUrl = i.imageUrl;
          if (title && !title.includes(' — ')) existing.title = title;
        }
        return Array.from(byPid.values()).map((g) => ({
          ...g,
          allowedVariantIds: g.allowedVariantIds.length
            ? g.allowedVariantIds
            : undefined,
          selectedVariants: g.selectedVariants.length
            ? g.selectedVariants
            : undefined,
          // Single selected variant → pin it for auto-add
          variantId:
            g.allowedVariantIds.length === 1
              ? g.allowedVariantIds[0]
              : g.variantId,
        }));
      };

      const buyProductIdsClean =
        buyScope === 'products'
          ? [...new Set(buySelected.map((i) => productKey(i)).filter(Boolean))]
          : [];

      const groupedGetProducts =
        effectiveGetScope === 'products'
          ? groupGetItemsByProduct(effectiveGetSelected)
          : [];

      const config = {
        buyXGetY: {
          offerMode: offerMode || 'bxgy',
          /** sameSku true = BOGO (free gift is the same product) */
          sameSku: isBogo,
          mixAndMatch: !isBogo,
          buyQuantity: Number(buyQuantity) || 1,
          /** Base Y = free units (BOGO) or sum of per-product gift quantities (BXGY) */
          getQuantity: freeY,
          buyScope,
          getScope: effectiveGetScope,
          buyProductIds: buyProductIdsClean,
          // Do not send buyVariantIds for unlock — any variant of the product counts
          buyVariantIds: [] as string[],
          buyCollectionIds:
            buyScope === 'collections' ? buySelected.map((i) => i.id) : [],
          getProductIds:
            effectiveGetScope === 'products'
              ? groupedGetProducts.map((g) => String(g.productId || g.id || ''))
              : [],
          getVariantIds:
            effectiveGetScope === 'products'
              ? groupedGetProducts.flatMap((g) => {
                  const allowed = g.allowedVariantIds as string[] | undefined;
                  if (allowed?.length) return allowed;
                  const v = g.variantId as string | null | undefined;
                  return v ? [v] : [];
                })
              : [],
          getCollectionIds:
            effectiveGetScope === 'collections'
              ? effectiveGetSelected
                  .filter(
                    (i) =>
                      i.kind === 'collection' ||
                      String(i.id).includes('/Collection/') ||
                      (!String(i.id).includes('/Product/') &&
                        !String(i.id).includes('/ProductVariant/')),
                  )
                  .map((i) => i.id)
              : [],
          buyItems: buySelected,
          getItems:
            effectiveGetScope === 'collections'
              ? effectiveGetSelected
                  .filter(
                    (i) =>
                      i.kind === 'collection' ||
                      String(i.id).includes('/Collection/') ||
                      (!String(i.id).includes('/Product/') &&
                        !String(i.id).includes('/ProductVariant/')),
                  )
                  .map((i) => ({
                    ...i,
                    kind: 'collection' as const,
                    quantity: i.quantity ?? 1,
                  }))
              : groupedGetProducts,
          // BOGO always caps free units to Y; BXGY uses optional gift limit
          giftLimitEnabled: isBogo ? true : giftLimitEnabled,
          giftLimitCount: isBogo
            ? freeY
            : giftLimitEnabled
              ? Math.max(1, Number(giftLimitCount) || 1)
              : null,
          // Multiplier removed — always one reward set per unlock (no scaling by sets)
          multiplierEnabled: false,
          // BXGY (different products): always customer chooses — no automatic add
          autoAdd: isBogo && giftMethod === 'automatic',
          selectionMode:
            isBogo && giftMethod === 'automatic' ? 'auto' : 'manual',
          discountPercent:
            discountType === 'free' ? 100 : Number(discountValue) || 100,
          discount: {
            code: uniqueCode,
            type: discountType,
            value: Number(discountValue),
          },
        },
        display: {
          giftMethod:
            isBogo && giftMethod === 'automatic'
              ? 'automatic'
              : 'customer_chooses',
          chooseUi:
            isBogo && giftMethod === 'automatic' ? null : 'popup',
          title: widgetTitle,
          description: widgetDescription,
          widgetType: 'POPUP',
          congratsBars,
          congratsTitle: congratsTitle.trim() || DEFAULT_CONGRATS_DESIGN.title,
          congratsSubtitle:
            congratsSubtitle.trim() || DEFAULT_CONGRATS_DESIGN.subtitle,
          promoWidgetEnabled: promoWidgetEnabled === true,
          promoWidgetTitle: promoWidgetTitle.trim() || DEFAULT_PROMO_TITLE,
          promoWidgetSubtitle:
            promoWidgetSubtitle.trim() || DEFAULT_PROMO_SUBTITLE_BXGY,
        },
        stacking: {
          priority: Math.max(0, Math.floor(Number(priority) || 0)),
          stopOnMatch,
        },
        advanced: {
          usageLimitEnabled,
          totalUses:
            usageLimitEnabled && totalUses.trim()
              ? Math.max(1, Math.floor(Number(totalUses)) || 1)
              : null,
          maxUsesPerCustomer: Math.max(
            1,
            Math.floor(Number(maxUsesPerCustomer)) || 1,
          ),
          customerEligibility: {
            type: eligibility,
            segments:
              eligibility === 'segments'
                ? segmentNamesFromValue(segmentValue)
                : [],
            segmentIds:
              eligibility === 'segments'
                ? segmentIdsFromValue(segmentValue)
                : [],
            utm: eligibility === 'utm' ? utmValue.trim() : null,
            locationCodes: eligibility === 'location' ? locationCodes : [],
            locations:
              eligibility === 'location'
                ? locationCodes.map((code) => ({
                    code,
                    name: locationLabels[code] || code,
                  }))
                : [],
          },
        },
      };

      const { startsAt, endsAt } = scheduleToIso(schedule);
      const payload = {
        name:
          name ||
          (isBogo ? 'Buy One Get One' : 'Buy X Get Y'),
        description: isBogo
          ? `BOGO · Buy ${buyQuantity} get ${freeY} free (same product)`
          : `BXGY · Buy ${buyQuantity} get ${totalGetQty}`,
        type: 'BUY_X_GET_Y' as const,
        priority: Math.max(0, Math.floor(Number(priority) || 0)),
        status: 'ACTIVE',
        startsAt: startsAt || null,
        endsAt: endsAt || null,
        config,
        tags: isBogo ? ['buy_x_get_y', 'bogo', 'same_sku'] : ['buy_x_get_y', 'bxgy'],
        maxUsesTotal:
          usageLimitEnabled && totalUses.trim()
            ? Math.max(1, Math.floor(Number(totalUses)) || 1)
            : null,
        maxUsesPerCustomer: Math.max(
          1,
          Math.floor(Number(maxUsesPerCustomer)) || 1,
        ),
      };

      if (campaignId) {
        await api.patch(`/campaigns/${campaignId}`, payload);
        markClean(formSnapshot);
        setMessage(t("Campaign saved"));
        showAppToast(t("Campaign saved"));
      } else {
        const created = await api.post<{ id: string }>('/campaigns', payload);
        markClean(formSnapshot);
        setMessage(t("Campaign saved"));
        showAppToast(t("Campaign saved"));
        navigate(`/campaigns/${created.id}?preset=buy_x_get_y`);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
      showAppToast(msg, { isError: true });
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    const b = getBaseline();
    if (!b) return;
    setOfferMode(b.offerMode);
    setName(b.name);
    setSchedule(b.schedule);
    setBuyQuantity(b.buyQuantity);
    setBuyScope(b.buyScope);
    setBuySelected(b.buySelected);
    setGetScope(b.getScope);
    setGetSelected(b.getSelected);
    setGetFreeQuantity(b.getFreeQuantity);
    setDiscountType(b.discountType);
    setDiscountValue(b.discountValue);
    setDiscountCode(b.discountCode);
    setGiftLimitEnabled(b.giftLimitEnabled);
    setGiftLimitCount(b.giftLimitCount);
    setPriority(b.priority);
    setStopOnMatch(b.stopOnMatch);
    setUsageLimitEnabled(b.usageLimitEnabled);
    setTotalUses(b.totalUses);
    setMaxUsesPerCustomer(b.maxUsesPerCustomer);
    setEligibility(b.eligibility);
    setSegmentValue(b.segmentValue);
    setUtmValue(b.utmValue);
    setLocationCodes(b.locationCodes);
    setLocationLabels(b.locationLabels);
    setGiftMethod(b.giftMethod);
    setWidgetTitle(b.widgetTitle);
    setWidgetDescription(b.widgetDescription);
    setCongratsBars(b.congratsBars !== false);
    setCongratsTitle(b.congratsTitle || DEFAULT_CONGRATS_DESIGN.title);
    setCongratsSubtitle(b.congratsSubtitle || DEFAULT_CONGRATS_DESIGN.subtitle);
    setError(null);
    setMessage(null);
    markCleanAfterDiscard(b);
  }

  function renderDiscountConfig() {
    return (
      <Box padding="300" background="bg-surface-secondary" borderRadius="200">
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm">
            {t("Discount config")}
          </Text>
          <TextField
            label={t("Discount code")}
            value={discountCode}
            onChange={(v) => setDiscountCode(v.toUpperCase().replace(/\s+/g, ''))}
            autoComplete="off"
            helpText={t("Auto-generated BUYXGETY_XXXXX. Unique across campaigns.")}
            placeholder="BUYXGETY_A3K9M"
          />
          <InlineStack gap="300" wrap>
            <div style={{ flex: 1, minWidth: 140 }}>
              <Select
                label={t("Type")}
                options={[
                  { label: t("Percentage"), value: 'percentage' },
                  { label: t("Free (100%)"), value: 'free' },
                ]}
                value={discountType}
                onChange={(v) => {
                  setDiscountType(v);
                  if (v === 'free') setDiscountValue('100');
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <TextField
                label={t("Value")}
                type="number"
                value={discountValue}
                onChange={setDiscountValue}
                prefix="%"
                autoComplete="off"
                disabled={discountType === 'free'}
              />
            </div>
          </InlineStack>
        </BlockStack>
      </Box>
    );
  }

  function renderBuys() {
    // BOGO: same product — pick products/collections once, buy X get Y free of those products
    if (isBogo) {
      return (
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">
            {t("Customer buys")}
          </Text>
          <Text as="p" tone="subdued">
            {t("Pick products or collections. Customers buy X units and get Y free of\n            the same product — no separate gift product picker.")}
          </Text>

          <TextField
            label={t("Buy quantity (X)")}
            type="number"
            min={1}
            value={buyQuantity}
            onChange={setBuyQuantity}
            autoComplete="off"
            helpText={t("Units of the SAME product required (not mixed). Example: 1 = true Buy One Get One. 1×Product A + 1×Product B does NOT count as 2 — only 2×A or 2×B. Same products as BXGY still need this per-product qty for BOGO to unlock.")}
            requiredIndicator
          />

          <TextField
            label={t("Free quantity (Y)")}
            type="number"
            min={1}
            value={getFreeQuantity}
            onChange={setGetFreeQuantity}
            autoComplete="off"
            helpText={t("How many free units of that same product they unlock (e.g. buy 1 get 1 free).")}
            requiredIndicator
          />

          {scopeSelect(buyScope, setBuyScope, buySelected, setBuySelected, 'buy')}

          {renderDiscountConfig()}
        </BlockStack>
      );
    }

    return (
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t("Customer buys")}
        </Text>
        <Text as="p" tone="subdued">
          {t("Define what customers must purchase to unlock a different reward product.")}
        </Text>

        <TextField
          label={t("Quantity")}
          type="number"
          value={buyQuantity}
          onChange={setBuyQuantity}
          autoComplete="off"
          helpText={t("Number of products the customer must buy (X).")}
          requiredIndicator
        />

        {scopeSelect(buyScope, setBuyScope, buySelected, setBuySelected, 'buy')}
      </BlockStack>
    );
  }

  function renderGets() {
    // BOGO should never land here (nav hidden); keep safe fallback
    if (isBogo) {
      return renderBuys();
    }
    return (
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t("Customer gets")}
        </Text>
        <Text as="p" tone="subdued">
          {t("Select different reward products and set quantity on each product. Total gift\n          units =")} {totalGetQty || 0}.
        </Text>

        {scopeSelect(
          getScope,
          setGetScope,
          getSelected,
          setGetSelected,
          'get',
          true,
        )}

        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("Gift limit")}
            </Text>
            <Checkbox
              label={t("Limit how many gift products the customer can select")}
              checked={giftLimitEnabled}
              onChange={setGiftLimitEnabled}
              helpText={t("When on, the popup lets shoppers pick at most this many products. Each product still uses its own quantity (e.g. select 2 products at qty 2 → 4 free units).")}
            />
            <TextField
              label={t("Max products customer can select")}
              type="number"
              value={giftLimitCount}
              onChange={setGiftLimitCount}
              autoComplete="off"
              disabled={!giftLimitEnabled}
              helpText={
                giftLimitEnabled
                  ? 'Product pick limit — not free unit count. Unit total = sum of selected product quantities.'
                  : 'Turn on the toggle to set a product pick limit.'
              }
            />
          </BlockStack>
        </Box>

        {renderDiscountConfig()}
      </BlockStack>
    );
  }

  function selectOfferMode(mode: OfferMode) {
    setOfferMode(mode);
    if (mode === 'bogo') {
      setName((n) =>
        !n.trim() || n === 'Buy X Get Y' ? 'Buy One Get One' : n,
      );
      setSection('buys');
      // Buy One Get One → X=1 by default (mixed 1+1+1 cart still unlocks)
      setBuyQuantity('1');
      setGetFreeQuantity('1');
      // Same-product gifts are automatic by default
      setGiftMethod('automatic');
      setDiscountType('free');
      setDiscountValue('100');
    } else {
      setName((n) =>
        !n.trim() || n === 'Buy One Get One' ? 'Buy X Get Y' : n,
      );
      setSection('buys');
      // BXGY (different products): customer always chooses the gift — no auto-add
      setGiftMethod('customer_chooses');
    }
    setMainStep(1);
  }

  function renderModePicker() {
    return (
      <Card>
        <BlockStack gap="400">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">
              {t("Choose offer type")}
            </Text>
            <Text as="p" tone="subdued">
              {t("BXGY rewards a different product. BOGO gives free units of the same\n              product the customer buys.")}
            </Text>
          </BlockStack>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 16,
            }}
          >
            <ModeCard
              selected={offerMode === 'bxgy'}
              title={t("BXGY (Different products)")}
              subtitle={t("Buy from one set of products, get a different gift.")}
              icon={<ModeIconBxgy />}
              onClick={() => selectOfferMode('bxgy')}
            />
            <ModeCard
              selected={offerMode === 'bogo'}
              title={t("BOGO (Same product)")}
              subtitle={t("Buy X get Y free of the same product or collection.")}
              icon={<ModeIconBogo />}
              onClick={() => selectOfferMode('bogo')}
            />
          </div>
        </BlockStack>
      </Card>
    );
  }

  function renderStacking() {
    return (
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t("Priority & stacking")}
        </Text>
        <TextField
          label={t("Priority")}
          type="number"
          value={priority}
          onChange={setPriority}
          autoComplete="off"
          helpText={t("Higher number wins when multiple campaigns unlock. Only the winner’s free products and coupon apply.")}
        />
        <Checkbox
          label={t("Don't combine with other Freebi offers")}
          checked={stopOnMatch}
          onChange={setStopOnMatch}
          helpText={t("When this campaign unlocks, lower-priority Freebi campaigns won't add gifts or apply their coupon. Turn off only if you intentionally want Freebi offers to stack.")}
        />
      </BlockStack>
    );
  }

  function renderAdvanced() {
    return (
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t("Advanced settings")}
        </Text>
        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("Usage limit")}
            </Text>
            <Checkbox
              label={t("Limit total uses")}
              checked={usageLimitEnabled}
              onChange={setUsageLimitEnabled}
            />
            <TextField
              label={t("Total uses")}
              type="number"
              value={totalUses}
              onChange={setTotalUses}
              disabled={!usageLimitEnabled}
              autoComplete="off"
            />
            <TextField
              label={t("Max uses per customer")}
              type="number"
              min={1}
              value={maxUsesPerCustomer}
              onChange={(v) => {
                if (v === '') {
                  setMaxUsesPerCustomer('');
                  return;
                }
                const n = Math.floor(Number(v));
                setMaxUsesPerCustomer(
                  Number.isFinite(n) && n >= 1 ? String(n) : '1',
                );
              }}
              autoComplete="off"
              helpText={t("How many times each customer can use this offer (default 1 = once).")}
            />
          </BlockStack>
        </Box>
        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("Customer eligibility")}
            </Text>
            {(
              [
                ['all', t('All customers')],
                ['segments', t('Specific customer segments')],
                ['utm', t('UTM link')],
                ['location', t('Customer location')],
              ] as const
            ).map(([value, label]) => (
              <RadioButton
                key={value}
                label={label}
                checked={eligibility === value}
                id={`bxgy-elig-${value}`}
                name="bxgy-eligibility"
                onChange={() => setEligibility(value)}
              />
            ))}
            {eligibility === 'segments' && (
              <CustomerSegmentField
                value={segmentValue}
                onChange={setSegmentValue}
              />
            )}
            {eligibility === 'utm' && (
              <TextField
                label="UTM"
                value={utmValue}
                onChange={setUtmValue}
                autoComplete="off"
              />
            )}
            {eligibility === 'location' && (
              <BlockStack gap="200">
                <InlineStack gap="200" blockAlign="center">
                  <Text as="span" tone="subdued">
                    {locationCodes.length
                      ? `${locationCodes.length} location(s) selected`
                      : 'No locations selected'}
                  </Text>
                  <Button onClick={() => setLocationPickerOpen(true)}>
                    {locationCodes.length ? 'Edit locations' : 'Browse locations'}
                  </Button>
                </InlineStack>
                {locationCodes.length > 0 && (
                  <InlineStack gap="100" wrap>
                    {locationCodes.map((code) => (
                      <Tag
                        key={code}
                        onRemove={() => {
                          setLocationCodes((prev) =>
                            prev.filter((c) => c !== code),
                          );
                          setLocationLabels((prev) => {
                            const next = { ...prev };
                            delete next[code];
                            return next;
                          });
                        }}
                      >
                        {locationLabels[code] || code}
                      </Tag>
                    ))}
                  </InlineStack>
                )}
                <Text as="p" tone="subdued" variant="bodySm">
                  {t("Click × on a tag to remove a location.")}
                </Text>
              </BlockStack>
            )}
          </BlockStack>
        </Box>
        <CampaignScheduleFields value={schedule} onChange={setSchedule} />
      </BlockStack>
    );
  }

  function methodCard(
    active: boolean,
    onClick: () => void,
    icon: string,
    title: string,
    subtitle: string,
  ) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          padding: 16,
          borderRadius: 12,
          border: active ? '1.5px solid #008060' : '1px solid #e1e3e5',
          background: active ? '#f6fef9' : '#fff',
          cursor: 'pointer',
        }}
      >
        <InlineStack gap="300" blockAlign="start" wrap={false}>
          <div
            style={{
              width: 18,
              height: 18,
              marginTop: 2,
              borderRadius: '50%',
              border: active ? '5px solid #008060' : '2px solid #8c9196',
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          />
          <div>
            <Text as="p" fontWeight="semibold">
              {icon} {title}
            </Text>
            <Text as="p" tone="subdued" variant="bodySm">
              {subtitle}
            </Text>
          </div>
        </InlineStack>
      </button>
    );
  }

  function renderDisplay() {
    const campaignReturn = `/campaigns/new/configure?type=BUY_X_GET_Y&preset=buy_x_get_y&currency=${currency}`;
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 320px',
          gap: 16,
        }}
      >
        <BlockStack gap="400">
          {/* BXGY (different products): no "Automatically" — customer always picks the gift */}
          {!isBogo && (
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {t("Gift selection method")}
                </Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  {t("Customers choose their free gift in a pop-up when the offer unlocks.")}
                </Text>
                {methodCard(
                  true,
                  () => setGiftMethod('customer_chooses'),
                  '🎁',
                  t('Customer chooses'),
                  t('Customer picks reward products in a pop-up'),
                )}
              </BlockStack>
            </Card>
          )}
          {isBogo && (
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {t("Gift selection method")}
                </Text>
                {methodCard(
                  giftMethod === 'automatic',
                  () => setGiftMethod('automatic'),
                  '⚡',
                  t('Automatically'),
                  t('Reward products are added or applied when buy conditions are met'),
                )}
                {methodCard(
                  giftMethod === 'customer_chooses',
                  () => setGiftMethod('customer_chooses'),
                  '🎁',
                  t('Customer chooses'),
                  t('Customer picks reward products in a pop-up'),
                )}
              </BlockStack>
            </Card>
          )}
          {(giftMethod === 'customer_chooses' || !isBogo) && (
            <Card>
              <BlockStack gap="300">
                <TextField
                  label={t("Title")}
                  value={widgetTitle}
                  onChange={setWidgetTitle}
                  autoComplete="off"
                  helpText={t("Shown on the free-gift chooser for this campaign")}
                />
                <TextField
                  label={t("Description")}
                  value={widgetDescription}
                  onChange={setWidgetDescription}
                  multiline={3}
                  autoComplete="off"
                  helpText={t("Campaign-specific copy. Colors live under Widgets → Pop-up.")}
                />
                <Button
                  onClick={() =>
                    navigate(
                      `/widgets/popup?return=${encodeURIComponent(campaignReturn)}`,
                    )
                  }
                >
                  {t("Edit pop-up style")}
                </Button>
              </BlockStack>
            </Card>
          )}
          {isBogo && giftMethod === 'automatic' && (
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  {t("Cart Success Notification Bar")}
                </Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  {t("Toast when rewards are auto-added. Style under Widgets → Cart\n                  Success Notification Bar.")}
                </Text>
                <Checkbox
                  label={t("Show when gifts are added to cart")}
                  checked={congratsBars}
                  onChange={setCongratsBars}
                />
                {congratsBars && (
                  <>
                    <TextField
                      label={t("Title")}
                      value={congratsTitle}
                      onChange={setCongratsTitle}
                      autoComplete="off"
                      placeholder={DEFAULT_CONGRATS_DESIGN.title}
                    />
                    <TextField
                      label={t("Subtitle")}
                      value={congratsSubtitle}
                      onChange={setCongratsSubtitle}
                      autoComplete="off"
                      placeholder={DEFAULT_CONGRATS_DESIGN.subtitle}
                    />
                    <Button
                      onClick={() =>
                        navigate(
                          `/widgets/congrats?return=${encodeURIComponent(campaignReturn)}`,
                        )
                      }
                    >
                      {t("Edit Cart Success Notification Bar style")}
                    </Button>
                  </>
                )}
              </BlockStack>
            </Card>
          )}
          <CampaignPromoWidgetCard
            enabled={promoWidgetEnabled}
            onEnabledChange={setPromoWidgetEnabled}
            title={promoWidgetTitle}
            onTitleChange={setPromoWidgetTitle}
            subtitle={promoWidgetSubtitle}
            onSubtitleChange={setPromoWidgetSubtitle}
            triggerValue={String(Math.max(1, Number(buyQuantity) || 1))}
            gifts={getSelected
              .filter(
                (g) =>
                  g.kind !== 'collection' &&
                  !String(g.id || '').includes('/Collection/'),
              )
              .map((g) => {
                const orig = parseMoneyAmount(g.price);
                const offered =
                  orig == null
                    ? null
                    : offeredAmount(orig, discountType, discountValue);
                return {
                  id: String(g.productId || g.id),
                  title: String(g.title || 'Gift'),
                  imageUrl: g.imageUrl,
                  priceLabel:
                    orig != null
                      ? formatMoney(orig)
                      : g.price
                        ? String(g.price)
                        : null,
                  offeredLabel:
                    offered == null
                      ? null
                      : offered <= 0.005
                        ? 'Free'
                        : formatMoney(offered),
                };
              })}
            campaignReturn={campaignReturn}
          />
        </BlockStack>
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              {t("Preview")}
            </Text>
            {giftMethod === 'customer_chooses' ? (
              <PopupPreview
                design={{
                  ...DEFAULT_POPUP_DESIGN,
                  title: widgetTitle,
                  description: widgetDescription,
                }}
                mode="mobile"
                selectedCount={giftLimitEnabled ? Number(giftLimitCount) || 1 : 1}
                gifts={
                  getSelected.length
                    ? // One card per product (variants collapse) for accurate popup preview
                      Array.from(
                        getSelected
                          .reduce((map, g) => {
                            const pid =
                              String(g.productId || '')
                                .replace(/\D/g, '') ||
                              (g.kind === 'variant'
                                ? ''
                                : String(g.id || '').replace(/\D/g, '')) ||
                              String(g.id || '');
                            if (!pid || map.has(pid)) {
                              if (pid && map.has(pid)) {
                                const cur = map.get(pid)!;
                                cur.meta = 'Multiple variants';
                              }
                              return map;
                            }
                            let title = String(g.title || 'Gift');
                            const sep = title.indexOf(' — ');
                            if (sep > 0) title = title.slice(0, sep).trim();
                            map.set(pid, {
                              id: pid,
                              title,
                              meta: `Qty ${g.quantity ?? 1}`,
                            });
                            return map;
                          }, new Map<string, { id: string; title: string; meta: string }>())
                          .values(),
                      )
                    : undefined
                }
              />
            ) : congratsBars ? (
              <CongratsPreview
                design={{
                  ...DEFAULT_CONGRATS_DESIGN,
                  title: congratsTitle,
                  subtitle: congratsSubtitle,
                }}
                mode="mobile"
                progressPercent={70}
              />
            ) : (
              <Text as="p" tone="subdued">
                {t("Buy")} {buyQuantity} {t("→ get")} {totalGetQty || 0} {t("gift unit")}
                {(totalGetQty || 0) === 1 ? '' : 's'} {t("applied automatically.")}
              </Text>
            )}
            <Text as="p" fontWeight="semibold">
              {t("Buy")} {buyQuantity} get {totalGetQty || 0}
            </Text>
          </BlockStack>
        </Card>
      </div>
    );
  }

  function renderMain() {
    if (section === 'gets') return renderGets();
    if (section === 'stacking') return renderStacking();
    if (section === 'advanced') return renderAdvanced();
    return renderBuys();
  }

  const pageLabel = campaignPageLabel(
    isEdit,
    isBogo ? 'Buy One Get One' : 'Buy X Get Y',
  );

  return (
    <Page
      title={pageLabel}
      subtitle={
        mainStep === 0
          ? 'Choose BXGY or BOGO'
          : name.trim() || undefined
      }
      backAction={{
        content: mainStep === 0 ? 'Campaigns' : 'Back',
        onAction: () => {
          if (mainStep === 0 || isEdit) navigate('/campaigns');
          else if (mainStep === 2) {
            setMainStep(1);
            setSection('advanced');
          } else if (section !== 'buys') {
            const i = sideNavOrder.indexOf(section);
            if (i > 0) setSection(sideNavOrder[i - 1]);
            else if (!isEdit) setMainStep(0);
            else navigate('/campaigns');
          } else if (!isEdit) setMainStep(0);
          else navigate('/campaigns');
        },
      }}
      primaryAction={
        !embedded && mainStep !== 0
          ? {
              content: t("Save"),
              loading: saving,
              disabled: !dirty || !offerMode,
              onAction: () => void save(true),
            }
          : undefined
      }
    >
      <ContextualSaveBar
        id="freebi-bxgy-save"
        open={
          embedded &&
          dirty &&
          !loadingCampaign &&
          mainStep !== 0 &&
          Boolean(offerMode)
        }
        saving={saving}
        onSave={() => save(true)}
        onDiscard={discard}
      />
      <SavingOverlay
        open={saving}
        label={t("Saving campaign…")}
        detail="Syncing BOGO rules and Shopify discounts"
      />
      <BlockStack gap="400">
        <CampaignNameEditor
          name={name}
          onChange={setName}
          placeholder={t("Buy X Get Y")}
          defaultEditing={!isEdit && !name.trim()}
        />
        {loadingCampaign && <Banner tone="info">{t("Loading campaign…")}</Banner>}
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {message && (
          <Banner tone="success" onDismiss={() => setMessage(null)}>
            {message}
          </Banner>
        )}

        {mainStep === 0 ? (
          renderModePicker()
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isEdit || offerMode ? 'auto 1fr 1fr' : '1fr 1fr',
                gap: 8,
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #e1e3e5',
                padding: 6,
                alignItems: 'stretch',
              }}
            >
              {!isEdit && (
                <button
                  type="button"
                  onClick={() => setMainStep(0)}
                  style={{
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 16px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 13,
                    background: 'transparent',
                    color: '#202223',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t("Offer type")}
                  {offerMode
                    ? ` · ${isBogo ? 'BOGO' : 'BXGY'}`
                    : ''}
                </button>
              )}
              {([1, 2] as const).map((step) => (
                <button
                  key={step}
                  type="button"
                  onClick={() => {
                    if (!offerMode && !isEdit) {
                      setMainStep(0);
                      return;
                    }
                    setMainStep(step);
                  }}
                  style={{
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 16px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 13,
                    background: mainStep === step ? '#e4e5e7' : 'transparent',
                    color: '#202223',
                  }}
                >
                  {t("Step")} {step}:{' '}
                  {step === 1 ? t('Campaign setting') : t('Display setting')}
                </button>
              ))}
            </div>

            {mainStep === 2 ? (
              <BlockStack gap="400">
                {renderDisplay()}
                <InlineStack align="start">
                  <Button
                    onClick={() => {
                      setMainStep(1);
                      setSection('advanced');
                    }}
                  >
                    {t("Back to campaign settings")}
                  </Button>
                </InlineStack>
              </BlockStack>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) 280px',
                  gap: 16,
                  alignItems: 'start',
                }}
              >
                <Card padding="0">
                  <div
                    style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}
                  >
                    <div
                      style={{
                        borderRight: '1px solid #e1e3e5',
                        padding: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                      }}
                    >
                      {sideNavItem('buys', t('Customer buys'), '🛒')}
                      {!isBogo &&
                        sideNavItem('gets', t('Customer gets'), '🎁')}
                      {sideNavItem(
                        'stacking',
                        t('Priority & stacking'),
                        '🔗',
                      )}
                      {sideNavItem('advanced', t('Advanced settings'), '⚙️')}
                    </div>
                    <div style={{ padding: 20 }}>
                      {renderMain()}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: 8,
                          marginTop: 24,
                          paddingTop: 16,
                          borderTop: '1px solid #e1e3e5',
                        }}
                      >
                        <Button
                          disabled={section === 'buys'}
                          onClick={() => {
                            const i = sideNavOrder.indexOf(section);
                            if (i > 0) setSection(sideNavOrder[i - 1]);
                          }}
                        >
                          {t("Back")}
                        </Button>
                        <Button
                          variant="primary"
                          disabled={
                            (section === 'buys' && !canGoNextFromBuys) ||
                            (section === 'gets' && !canGoNextFromGets)
                          }
                          onClick={() => {
                            const i = sideNavOrder.indexOf(section);
                            if (i < sideNavOrder.length - 1) {
                              setSection(sideNavOrder[i + 1]);
                            } else setMainStep(2);
                          }}
                        >
                          {t("Next")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card>
                  <BlockStack gap="300">
                    <Text as="h2" variant="headingMd">
                      {t("Summary")}
                    </Text>
                    <SummaryBlock
                      title={t("Offer type")}
                      lines={[
                        {
                          label: isBogo
                            ? 'BOGO (same product)'
                            : 'BXGY (different products)',
                        },
                      ]}
                      onClick={
                        !isEdit ? () => setMainStep(0) : undefined
                      }
                    />
                    <SummaryBlock
                      title={t("Basic information")}
                      lines={[
                        {
                          label:
                            name ||
                            (isBogo ? 'Buy One Get One' : 'Buy X Get Y'),
                        },
                        {
                          label: t('Start: {{date}}', {
                            date: `${schedule.startDate}${schedule.setEndDate && schedule.endDate ? ` → ${schedule.endDate}` : ''}`,
                          }),
                        },
                      ]}
                    />
                    <SummaryBlock
                      title={t("Customer buys")}
                      lines={[
                        { label: `Buy quantity: ${buyQuantity}` },
                        ...(isBogo
                          ? [{ label: `Free quantity: ${getFreeQuantity}` }]
                          : []),
                        {
                          label:
                            buyScope === 'products'
                              ? `${buySelected.length} product(s)`
                              : `${buySelected.length} collection(s)`,
                        },
                      ]}
                      onClick={() => setSection('buys')}
                    />
                    {!isBogo && (
                      <SummaryBlock
                        title={t("Customer gets")}
                        lines={[
                          {
                            label:
                              getScope === 'products'
                                ? `${getSelected.length} product(s) · ${totalGetQty} unit(s)`
                                : `${getSelected.length} collection(s) · ${totalGetQty} unit(s)`,
                          },
                          {
                            label: giftLimitEnabled
                              ? `Gift limit: ${giftLimitCount}`
                              : 'Gift limit: off',
                          },
                        ]}
                        onClick={() => setSection('gets')}
                      />
                    )}
                  </BlockStack>
                </Card>
              </div>
            )}
          </>
        )}
      </BlockStack>

      <LocationPickerModal
        open={locationPickerOpen}
        selectedCodes={locationCodes}
        onClose={() => setLocationPickerOpen(false)}
        onConfirm={(selection) => {
          setLocationCodes(selection.map((s) => s.code));
          setLocationLabels(
            Object.fromEntries(selection.map((s) => [s.code, s.name])),
          );
        }}
      />

      <CatalogPickerModal
        open={browseOpen}
        mode={browseScope === 'products' ? 'products' : 'collections'}
        title={
          browseTarget === 'buy'
            ? browseScope === 'products'
              ? 'Select products customers buy'
              : 'Select collections customers buy'
            : browseScope === 'products'
              ? 'Select gift products'
              : 'Select gift collections'
        }
        selectedIds={pickerSelectedIds(
          browseTarget === 'buy' ? buySelected : getSelected,
        )}
        onClose={() => setBrowseOpen(false)}
        onToggle={togglePickerItem}
        onToggleMany={togglePickerMany}
      />
    </Page>
  );
}

function SummaryBlock({
  title,
  lines,
  onClick,
}: {
  title: string;
  lines: Array<{ label: string }>;
  onClick?: () => void;
}) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <Text as="span" fontWeight="semibold">
        {title}
      </Text>
      <div
        style={{
          marginTop: 8,
          padding: 10,
          borderRadius: 10,
          background: '#f6f6f7',
        }}
      >
        <BlockStack gap="100">
          {lines.map((l, i) => (
            <Text key={`${l.label}-${i}`} as="p" variant="bodySm">
              {l.label}
            </Text>
          ))}
        </BlockStack>
      </div>
    </div>
  );
}

function ModeCard({
  selected,
  title,
  subtitle,
  icon,
  onClick,
}: {
  selected: boolean;
  title: string;
  subtitle: string;
  icon: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: '28px 20px',
        borderRadius: 14,
        border: selected ? '2px solid #2c6ecb' : '1.5px solid #c9cccf',
        background: selected ? '#f2f7fe' : '#fff',
        boxShadow: selected ? '0 0 0 1px #2c6ecb' : 'none',
        cursor: 'pointer',
        textAlign: 'center',
        minHeight: 160,
        transition: 'border-color .15s, background .15s, box-shadow .15s',
      }}
    >
      <div style={{ marginBottom: 8 }}>{icon}</div>
      <Text as="p" fontWeight="semibold" variant="bodyMd">
        {title}
      </Text>
      <Text as="p" tone="subdued" variant="bodySm">
        {subtitle}
      </Text>
    </button>
  );
}

/** BXGY — necktie + jeans (different products) */
function ModeIconBxgy() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 36,
        lineHeight: 1,
        letterSpacing: 2,
      }}
    >
      <span>👔</span>
      <span style={{ fontSize: 22, fontWeight: 700, color: '#8c9196' }}>+</span>
      <span>👖</span>
    </span>
  );
}

/** BOGO — necktie + necktie (same product) */
function ModeIconBogo() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 36,
        lineHeight: 1,
        letterSpacing: 2,
      }}
    >
      <span>👔</span>
      <span style={{ fontSize: 22, fontWeight: 700, color: '#8c9196' }}>+</span>
      <span>👔</span>
    </span>
  );
}
